import React, { useEffect, useRef, useState } from "react";
import { IonHeader, IonBackButton, IonMenuButton } from "@ionic/react";
import { Anchor, Menu, Play, Plus, Umbrella } from "lucide-react";
import { useAuth } from "@/features/auth/hooks";
import { useResponsive } from "@/hooks/use-responsive";
import { useIonRouter } from "@/app/IonicContextProvider";

type HeaderProps = {
  title?: string;
  showBackButton?: boolean;
  /** Where to go if there's no history to pop */
  backHref?: string;
};

function Header({ title, showBackButton = false, backHref = "/shifts" }: HeaderProps) {
  const { user } = useAuth();
  const router = useIonRouter();
  const { isDesktop } = useResponsive();
  const appName = import.meta.env.VITE_APP_NAME || "TightShip";
  const [isQuickActionsOpen, setIsQuickActionsOpen] = useState(false);
  const quickActionsRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isQuickActionsOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (!quickActionsRef.current?.contains(event.target as Node)) {
        setIsQuickActionsOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
    };
  }, [isQuickActionsOpen]);

  const handleRequestLeave = () => {
    setIsQuickActionsOpen(false);
    router.push("/shifts?openLeaveModal=1", "root", "replace");
  };

  const handleStartShift = () => {
    setIsQuickActionsOpen(false);
    router.push("/shifts?openStartShift=1", "root", "replace");
  };

  return (
    <IonHeader className="ion-no-border">
      <div
        className="relative flex items-center justify-between border-b border-slate-100 bg-white px-4 pb-4 pt-[max(1rem,var(--ion-safe-area-top))]"
        data-testid="header-shell"
      >
        <div className="flex w-8 items-center justify-center">
          {showBackButton ? (
            <IonBackButton defaultHref={backHref} text="" />
          ) : !isDesktop ? (
            <IonMenuButton
              autoHide={false}
              className="h-8 w-8 text-slate-500 transition-all active:scale-95"
            >
              <Menu className="h-5 w-5" />
            </IonMenuButton>
          ) : null}
        </div>

        <div className="pointer-events-none absolute left-1/2 flex -translate-x-1/2 items-center gap-2">
          {showBackButton ? (
            <span className="text-base font-semibold text-slate-800">
              {title ?? appName}
            </span>
          ) : (
            <>
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-500">
                <Anchor className="h-4 w-4 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-800">{appName}</span>
            </>
          )}
        </div>

        {user && !showBackButton ? (
          <div className="relative" ref={quickActionsRef}>
            <button
              type="button"
              onClick={() => setIsQuickActionsOpen((open) => !open)}
              className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600 text-white transition-all hover:bg-blue-700 active:scale-95"
              aria-haspopup="menu"
              aria-expanded={isQuickActionsOpen}
              aria-label="Open quick actions"
              data-testid="header-quick-actions-button"
            >
              <Plus className="h-4 w-4" />
            </button>
            {isQuickActionsOpen ? (
              <div
                className="absolute right-0 top-12 z-50 w-48 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg"
                role="menu"
                data-testid="header-quick-actions-menu"
              >
                <button
                  type="button"
                  onClick={handleRequestLeave}
                  className="flex w-full items-center gap-3 border-b border-slate-100 px-4 py-3 text-left transition-all hover:bg-slate-50 active:bg-slate-100"
                  role="menuitem"
                >
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-100">
                    <Umbrella className="h-4 w-4 text-indigo-600" />
                  </div>
                  <span className="text-sm font-semibold text-slate-800">
                    Request leave
                  </span>
                </button>
                <button
                  type="button"
                  onClick={handleStartShift}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left transition-all hover:bg-slate-50 active:bg-slate-100"
                  role="menuitem"
                >
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100">
                    <Play className="h-4 w-4 text-blue-600" />
                  </div>
                  <span className="text-sm font-semibold text-slate-800">
                    Start shift
                  </span>
                </button>
              </div>
            ) : null}
          </div>
        ) : (
          <div className="w-8" />
        )}
      </div>
    </IonHeader>
  );
}

export default Header;
